import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Spinner } from '@ixicare/ui';
import { Device, DeviceConfigurationCommonResponse, DeviceType, ProfileType } from '@ixicare/resources';
import connect from '../../../store/connect';
import DevicesStore from '~modules/devices/store/Devices';
import AuthStore from '~store/Auth';
import NotificationsStore from '~store/Notifications';
import { UserAction } from '~config/notification';
import { t } from '~i18n';
import { FieldValues, Role } from '../components/DeviceConfigForm/schema';
import { GatingContext } from '../components/DeviceConfigForm/utils/gating';
import { State } from '~store/types';
import DeviceConfigForm from '../components/DeviceConfigForm';
import { SettingsDiffering } from '../components/DeviceConfigForm/DeviceConfigForm';

interface Props {
  devicesStore: DevicesStore;
  authStore: AuthStore;
  notifications: NotificationsStore;
}

const profileTypeToRole = (pt: ProfileType | null | undefined): Role => {
  switch (pt) {
    case ProfileType.WatcherrEmployee:
      return 'WATCHERR_EMPLOYEE';
    case ProfileType.Installer:
      return 'INSTALLER';
    default:
      return 'INSTALLER';
  }
};

const DeviceBulkConfigContainer: React.FC<Props> = ({ devicesStore, authStore, notifications }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const uuids = React.useMemo(() => (searchParams.get('devices') ?? '').split(',').filter(Boolean), [searchParams]);
  const deviceType = searchParams.get('type') as DeviceType | null;

  const [common, setCommon] = React.useState<FieldValues | null>(null);
  const [macs, setMacs] = React.useState<string[]>([]);
  const [differing, setDiffering] = React.useState<SettingsDiffering | undefined>(undefined);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  // Fetch the settings common to the selected devices.
  React.useEffect(() => {
    if (uuids.length === 0) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    Device.getDevicesSettings({ deviceUuid: uuids, listCommon: true })
      .then((res) => {
        if (cancelled) return;
        const resp = res as DeviceConfigurationCommonResponse;
        setCommon(resp.common ?? {});
        setMacs(Object.values(resp.devices ?? {}).map((d) => d.mac_address));
        setDiffering({
          byKey: resp.differing ?? {},
          macById: Object.fromEntries(Object.entries(resp.devices ?? {}).map(([id, d]) => [id, d.mac_address])),
        });
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uuids]);

  // Load the schema for the (single) device type of the selection.
  React.useEffect(() => {
    if (!authStore.userType || !deviceType) return;
    devicesStore.reset();
    devicesStore.loadConfigSchema(authStore.userType, deviceType);
  }, [authStore.userType, deviceType, devicesStore]);

  if (uuids.length === 0 || !deviceType) {
    return <Alert type="error">{t('device-config.error.load', { message: 'No devices selected.' })}</Alert>;
  }

  if (devicesStore.schemaError || error) {
    return (
      <Alert type="error">{t('device-config.error.load', { message: 'Could not load device configuration.' })}</Alert>
    );
  }

  if (devicesStore.schemaLoading || !devicesStore.schema || loading || common === null) {
    return <Spinner />;
  }

  const gating: GatingContext = {
    role: profileTypeToRole(authStore.userType),
    permissions: ['can_edit_device'],
  };
  const isInstaller = authStore.userType === ProfileType.Installer;

  const onSubmit = async (payload: FieldValues) => {
    const results = await Promise.allSettled(uuids.map((u) => Device.postDeviceSettings(u, payload)));
    const failures = results.filter((r) => r.status === 'rejected');
    if (failures.length) {
      notifications.addUserActionNotification({
        type: UserAction.DeviceConfigUpdate,
        notificationType: 'error',
        message: t('alerts.device.config.update.failure'),
      });
      // Keep the form dirty so the user can retry; don't navigate away.
      throw new Error('Bulk settings update failed');
    }
    notifications.addUserActionNotification({
      type: UserAction.DeviceConfigUpdate,
      notificationType: 'success',
      message: t('alerts.device.config.update.success'),
    });
    navigate('/device-overview');
  };

  return (
    <DeviceConfigForm
      schema={devicesStore.schema}
      configValues={common}
      deviceMacs={macs}
      isInstaller={isInstaller}
      gating={gating}
      onSubmit={onSubmit}
      differing={differing}
    />
  );
};

export default connect((state: State) => ({
  devicesStore: state.devices,
  authStore: state.auth,
  notifications: state.notifications,
}))(DeviceBulkConfigContainer);
